<template>
  <DxPopup
    :visible="visible"
    :full-screen="true"
    :show-title="false"
    :show-close-button="false"
    :wrapper-attr="{ class: 'parcel-map-popup' }"
    @hiding="close"
  >
    <div class="parcel-map-preview">
      <GoogleMap
        v-if="visible && !loading && layers.length"
        :key="loadSequence"
        :show-map="true"
        :init-map-in-mount="true"
        :read-only="true"
        :preview-layers="layers"
        map-class="parcel-map-preview-map"
      />
      <div v-else class="parcel-map-status" role="status">
        {{ loading ? $t('common.please_wait') : error || $t('common.no_data_found') }}
      </div>
      <button class="parcel-map-back" type="button" :title="$t('common.close')" :aria-label="$t('common.close')" @click="close">
        <i class="fa-solid fa-arrow-left" aria-hidden="true"></i>
      </button>
      <div class="parcel-map-close">
        <button class="sticky_action_button" type="button" @click="close">{{ $t('common.close') }}</button>
      </div>
    </div>
  </DxPopup>
</template>

<script>
import { mapGetters } from "vuex";
import DxPopup from "devextreme-vue/popup";
import GoogleMap from "./GoogleMap.vue";
import axios from "@/plugins/axios";
import { extractErrorMessage } from "@/helpers/generalActions";
import "@fortawesome/fontawesome-free/css/all.css";

export default {
  name: "ParcelMapPopup",
  components: { DxPopup, GoogleMap },
  props: {
    visible: { type: Boolean, default: false },
    parcelUuids: { type: Array, default: () => [] },
    parcelSeasons: { type: Object, default: () => ({}) },
  },
  data() {
    return { layers: [], loading: false, error: null, loadSequence: 0, destroyed: false };
  },
  computed: {
    ...mapGetters({ departments: "company/departments" }),
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        if (value) this.loadLayers();
        else {
          this.loadSequence += 1;
          this.loading = false;
        }
      },
    },
    parcelUuids: {
      deep: true,
      handler() { if (this.visible) this.loadLayers(); },
    },
    parcelSeasons: {
      deep: true,
      handler() { if (this.visible) this.loadLayers(); },
    },
  },
  beforeDestroy() {
    this.destroyed = true;
    this.loadSequence += 1;
  },
  methods: {
    close() {
      this.loadSequence += 1;
      this.loading = false;
      this.$emit("update:visible", false);
      this.$emit("close");
    },
    async loadLayers() {
      const sequence = ++this.loadSequence;
      const parcelUuids = [...new Set(this.parcelUuids.filter(Boolean))];
      this.layers = [];
      this.error = null;
      this.loading = true;
      try {
        if (!parcelUuids.length) return;
        const response = await axios.get("/layers", { params: {
          parcel_uuid: parcelUuids,
          season_uuid: [...new Set(parcelUuids.flatMap((uuid) => this.parcelSeasons[uuid] || []).filter(Boolean))],
          department_uuid: (this.departments || []).map((department) => department.uuid).join(","),
          limit: 9999,
        } });
        if (this.destroyed || sequence !== this.loadSequence || !this.visible) return;
        const payload = response.data.data;
        const layers = Array.isArray(payload) ? payload : payload?.data || [];
        this.layers = layers.filter((layer) => {
          const seasons = this.parcelSeasons[layer.parcel_uuid];
          return parcelUuids.includes(layer.parcel_uuid) && (!seasons?.length || seasons.includes(layer.season_uuid));
        });
      } catch (error) {
        if (!this.destroyed && sequence === this.loadSequence) this.error = extractErrorMessage(this, error);
      } finally {
        if (!this.destroyed && sequence === this.loadSequence) this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.parcel-map-popup .dx-popup-content { padding: 0; }
.parcel-map-preview { position: relative; width: 100%; height: 100%; }
.parcel-map-preview > .main-map-wrapper { height: 100%; }
.gmap-map.parcel-map-preview-map { width: 100%; height: 100%; min-height: 240px; }
.parcel-map-status { display: flex; align-items: center; justify-content: center; height: 100%; padding: 80px; text-align: center; overflow-wrap: anywhere; }
.parcel-map-back {
  position: absolute;
  top: calc(80px + env(safe-area-inset-top, 0px));
  left: calc(16px + env(safe-area-inset-left, 0px));
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 10px;
  border: 1px solid #d7ddd9;
  border-radius: 50%;
  background: #fff;
  color: #096c27;
  cursor: pointer;
}
.parcel-map-back:focus-visible { outline: 2px solid #096c27; outline-offset: 2px; }
.parcel-map-close {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
  display: flex;
  justify-content: center;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
}
.parcel-map-close .sticky_action_button {
  position: static;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 320px;
  min-height: 44px;
  margin: 0;
  border: 0;
  border-radius: 6px;
  background: #096c27;
  color: #fff;
  cursor: pointer;
}
</style>